import React, { createContext, useContext, useReducer, ReactNode } from 'react';
import { IBiddingDetailResponse } from '../../../model/base/response/bidding-detail-response';

interface BiddingDetailState {
  bidding: IBiddingDetailResponse | null;
  loading: boolean;
  error: string | null;
}

type BiddingDetailAction =
  | { type: 'LOAD_START' }
  | { type: 'LOAD_SUCCESS'; payload: IBiddingDetailResponse }
  | { type: 'LOAD_ERROR'; payload: string };

const initialState: BiddingDetailState = {
  bidding: null,
  loading: false,
  error: null,
};

const BiddingDetailStateContext = createContext<BiddingDetailState | undefined>(undefined);
const BiddingDetailDispatchContext = createContext<React.Dispatch<BiddingDetailAction> | undefined>(undefined);

function biddingDetailReducer(state: BiddingDetailState, action: BiddingDetailAction): BiddingDetailState {
  switch (action.type) {
    case 'LOAD_START':
      return { ...state, loading: true, error: null };
    case 'LOAD_SUCCESS':
      return { ...state, loading: false, bidding: action.payload };
    case 'LOAD_ERROR':
      return { ...state, loading: false, error: action.payload };
    default:
      return state;
  }
}

export function BiddingDetailProvider({ children }: { children: ReactNode }) {
  const [state, dispatch] = useReducer(biddingDetailReducer, initialState);

  return (
    <BiddingDetailStateContext.Provider value={state}>
      <BiddingDetailDispatchContext.Provider value={dispatch}>
        {children}
      </BiddingDetailDispatchContext.Provider>
    </BiddingDetailStateContext.Provider>
  );
}

export function useBiddingDetailState() {
  const context = useContext(BiddingDetailStateContext);
  if (context === undefined) {
    throw new Error('useBiddingDetailState must be used within a BiddingDetailProvider');
  }
  return context;
}

export function useBiddingDetailDispatch() {
  const context = useContext(BiddingDetailDispatchContext);
  if (context === undefined) {
    throw new Error('useBiddingDetailDispatch must be used within a BiddingDetailProvider');
  }
  return context;
}
